import type { ReactNode } from "react";
import { getProductFacts, type ProductFactComponent, type ProductFacts } from "../data/productFacts";
import type { Product } from "../data/catalogue";

const unverifiedIngredientsMessage = "Ingredients not yet verified";

function FactBlock({
  title,
  children
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="product-fact">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function InciList({ items }: { items: string[] }) {
  return <p className="product-inci">{items.join(", ")}</p>;
}

function ComponentFacts({ component }: { component: ProductFactComponent }) {
  const heading = [component.name, component.packSize].filter(Boolean).join(" · ");

  return (
    <article className="product-fact-component">
      <h3>{heading}</h3>
      {component.ingredients ? (
        <InciList items={component.ingredients.items} />
      ) : (
        <p className="product-fact-unverified">{unverifiedIngredientsMessage}</p>
      )}
      {component.directions && <p>{component.directions.text}</p>}
      {component.warnings && <p>{component.warnings.text}</p>}
    </article>
  );
}

function CosmeticFacts({ facts }: { facts: ProductFacts }) {
  const hasComponentIngredients = facts.components.some((component) => component.ingredients);
  const showSetComponents = facts.kind === "set" && facts.components.length > 0;

  return (
    <>
      {facts.description && (
        <FactBlock title="Description">
          <p>{facts.description.text}</p>
        </FactBlock>
      )}
      <FactBlock title="Ingredients">
        {facts.ingredients ? (
          <InciList items={facts.ingredients.items} />
        ) : showSetComponents ? (
          <>
            {hasComponentIngredients ? (
              <p>
                Ingredient lists are shown only for set items that have been verified
                separately.
              </p>
            ) : null}
            {facts.components.map((component) => (
              <ComponentFacts key={`${component.name}-${component.packSize ?? ""}`} component={component} />
            ))}
          </>
        ) : facts.showIngredientsUnverified ? (
          <p className="product-fact-unverified">{unverifiedIngredientsMessage}</p>
        ) : null}
      </FactBlock>
      {facts.directions && (
        <FactBlock title="How to use">
          <p>{facts.directions.text}</p>
        </FactBlock>
      )}
      {facts.warnings && (
        <FactBlock title="Warnings">
          <p>{facts.warnings.text}</p>
        </FactBlock>
      )}
    </>
  );
}

function DeviceFactsView({ facts }: { facts: ProductFacts }) {
  return (
    <>
      {facts.description && (
        <FactBlock title="Description">
          <p>{facts.description.text}</p>
        </FactBlock>
      )}
      {facts.device?.specifications.length ? (
        <FactBlock title="Specifications">
          {facts.device.specifications.map((item) => (
            <p key={item.text}>{item.text}</p>
          ))}
        </FactBlock>
      ) : null}
      {facts.directions && (
        <FactBlock title="How to use">
          <p>{facts.directions.text}</p>
        </FactBlock>
      )}
      {(facts.warnings || facts.device?.safety.length) && (
        <FactBlock title="Warnings">
          {facts.warnings && <p>{facts.warnings.text}</p>}
          {facts.device?.safety.map((item) => (
            <p key={item.text}>{item.text}</p>
          ))}
        </FactBlock>
      )}
    </>
  );
}

interface ProductFactsSectionsProps {
  product: Product;
}

export function ProductFactsSections({ product }: ProductFactsSectionsProps) {
  const facts = getProductFacts(product.auroraId);
  if (!facts) {
    if (product.needsElectricalReview) return null;
    return (
      <div className="product-facts">
        <FactBlock title="Ingredients">
          <p className="product-fact-unverified">{unverifiedIngredientsMessage}</p>
        </FactBlock>
      </div>
    );
  }

  const hasContent =
    facts.description ||
    facts.ingredients ||
    facts.directions ||
    facts.warnings ||
    facts.components.length ||
    facts.device ||
    facts.showIngredientsUnverified;

  if (!hasContent) return null;

  return (
    <div className="product-facts">
      {facts.kind === "device" ? <DeviceFactsView facts={facts} /> : <CosmeticFacts facts={facts} />}
    </div>
  );
}
